"use client";

import {
  type EmojiPickerListCategoryHeaderProps,
  type EmojiPickerListEmojiProps,
  type EmojiPickerListRowProps,
  EmojiPicker as EmojiPickerPrimitive,
} from "frimousse";
import { LoaderIcon, SearchIcon } from "lucide-react";
import * as React from "react";

import { cn } from "@/lib/utils";

function EmojiPicker({
  className,
  ...props
}: React.ComponentProps<typeof EmojiPickerPrimitive.Root>) {
  return (
    <EmojiPickerPrimitive.Root
      className={cn(
        "bg-popover text-popover-foreground isolate flex h-full w-full flex-col overflow-hidden rounded-md",
        "min-h-0",
        className
      )}
      data-slot="emoji-picker"
      {...props}
    />
  );
}

function EmojiPickerSearch({
  className,
  ...props
}: React.ComponentProps<typeof EmojiPickerPrimitive.Search>) {
  return (
    <div
      className={cn("flex h-9 items-center gap-2 border-b px-3", className)}
      data-slot="emoji-picker-search-wrapper"
    >
      <SearchIcon className="size-4 shrink-0 opacity-50" />
      <EmojiPickerPrimitive.Search
        className="outline-hidden placeholder:text-muted-foreground flex h-10 w-full rounded-md bg-transparent py-3 text-sm disabled:cursor-not-allowed disabled:opacity-50"
        data-slot="emoji-picker-search"
        {...props}
      />
    </div>
  );
}

const EmojiPickerRow = React.memo(function EmojiPickerRow({
  children,
  ...props
}: EmojiPickerListRowProps) {
  return (
    <div {...props} className="scroll-my-1 px-1" data-slot="emoji-picker-row">
      {children}
    </div>
  );
});
EmojiPickerRow.displayName = "EmojiPickerRow";

const EmojiPickerEmoji = React.memo(function EmojiPickerEmoji({
  emoji,
  className,
  ...props
}: EmojiPickerListEmojiProps) {
  return (
    <button
      {...props}
      className={cn(
        "data-[active]:bg-accent flex size-7 items-center justify-center rounded-sm text-base",
        // CSS optimizations for GPU compositing - use transform instead of layout properties
        "will-change-transform [transform:translateZ(0)]",
        className
      )}
      data-slot="emoji-picker-emoji"
    >
      {emoji.emoji}
    </button>
  );
});
EmojiPickerEmoji.displayName = "EmojiPickerEmoji";

const EmojiPickerCategoryHeader = React.memo(
  function EmojiPickerCategoryHeader({
    category,
    ...props
  }: EmojiPickerListCategoryHeaderProps) {
    return (
      <div
        {...props}
        className="bg-popover text-muted-foreground px-3 pb-2 pt-3.5 text-xs leading-none"
        data-slot="emoji-picker-category-header"
      >
        {category.label}
      </div>
    );
  }
);
EmojiPickerCategoryHeader.displayName = "EmojiPickerCategoryHeader";

// Memoize the components object to prevent unnecessary re-renders
const emojiPickerComponents = {
  Row: EmojiPickerRow,
  Emoji: EmojiPickerEmoji,
  CategoryHeader: EmojiPickerCategoryHeader,
} as const;

function EmojiPickerContent({
  className,
  ...props
}: React.ComponentProps<typeof EmojiPickerPrimitive.Viewport>) {
  const viewportRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;

    // Handle wheel events to ensure scrolling works even when inside a Dialog
    const handleWheel = (e: WheelEvent) => {
      const { scrollTop, scrollHeight, clientHeight } = viewport;
      const isAtTop = scrollTop === 0;
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 1;

      // If we can scroll in the direction of the wheel, prevent default and scroll
      if ((e.deltaY > 0 && !isAtBottom) || (e.deltaY < 0 && !isAtTop)) {
        viewport.scrollTop += e.deltaY;
        e.preventDefault();
        e.stopPropagation();
      }
    };

    viewport.addEventListener('wheel', handleWheel, { passive: false });
    return () => {
      viewport.removeEventListener('wheel', handleWheel);
    };
  }, []);

  return (
    <EmojiPickerPrimitive.Viewport
      ref={viewportRef}
      className={cn(
        "outline-hidden relative flex-1 overflow-y-auto",
        // CSS optimizations for GPU compositing
        "will-change-transform [transform:translateZ(0)]",
        // Ensure scroll events work properly
        "[overscroll-behavior:contain]",
        className
      )}
      data-slot="emoji-picker-viewport"
      {...props}
    >
      <EmojiPickerPrimitive.Loading
        className="absolute inset-0 flex items-center justify-center text-muted-foreground"
        data-slot="emoji-picker-loading"
      >
        <LoaderIcon className="size-4 animate-spin" />
      </EmojiPickerPrimitive.Loading>
      <EmojiPickerPrimitive.Empty
        className="absolute inset-0 flex items-center justify-center text-muted-foreground text-sm"
        data-slot="emoji-picker-empty"
      >
        No emoji found.
      </EmojiPickerPrimitive.Empty>
      <EmojiPickerPrimitive.List
        className="select-none pb-1"
        components={emojiPickerComponents}
        data-slot="emoji-picker-list"
      />
    </EmojiPickerPrimitive.Viewport>
  );
}

// Memoize the footer render function to prevent unnecessary re-renders
const EmojiPickerFooterContent = React.memo(function EmojiPickerFooterContent({
  emoji,
}: {
  emoji: { emoji: string; label: string } | null;
}) {
  if (emoji) {
    return (
      <>
        <div className="flex size-7 flex-none items-center justify-center text-lg">
          {emoji.emoji}
        </div>
        <span className="text-secondary-foreground truncate text-xs">
          {emoji.label}
        </span>
      </>
    );
  }
  return (
    <span className="text-muted-foreground ml-1.5 flex h-7 items-center truncate text-xs">
      Select an emoji…
    </span>
  );
});
EmojiPickerFooterContent.displayName = "EmojiPickerFooterContent";

function EmojiPickerFooter({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      className={cn(
        "max-w-(--frimousse-viewport-width) flex w-full min-w-0 items-center gap-1 border-t p-2",
        className
      )}
      data-slot="emoji-picker-footer"
      {...props}
    >
      <EmojiPickerPrimitive.ActiveEmoji>
        {({ emoji }) => <EmojiPickerFooterContent emoji={emoji ?? null} />}
      </EmojiPickerPrimitive.ActiveEmoji>
    </div>
  );
}

export {
  EmojiPicker,
  EmojiPickerSearch,
  EmojiPickerContent,
  EmojiPickerFooter,
};